import type { IPolicy } from '@adornis/abac/core/types.js';
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { resolveGrantsFor } from '../grants.js';
import { permissionFor } from '../permissionName.js';

/**
 * A local stand-in for `@adornis/abac`'s `defineScopePolicy`.
 *
 * It exists for exactly one line. The package's `evaluate` hardcodes its grant
 * source as `resolvePermissionsFor(user._id)` on the server and
 * `resolveMyPermissions()` on the client — both of which read the
 * `role-assignments` collection — and offers no hook to supply grants from
 * anywhere else (`permissionResolver` only names the permission *string*). Since
 * grants are now a pure function of the user document, that read is the only part
 * that had to change.
 *
 * Everything else is deliberately identical to the package's behaviour, including
 * the payload shape, the first-check-to-pass-wins dispatch, and the
 * `${SCOPE}_${ACTION}` permission naming — so `AccessController`, `IPolicy`,
 * `ServerContextPolicy` and every existing check keep working untouched.
 *
 * The consequences of owning it are all improvements. Resolution is synchronous
 * and allocation-cheap instead of two Mongo round-trips per evaluation, so the
 * server memo that existed to survive `updateMany` fan-out is gone. On the client
 * it reads the live `currentUser()` subscription rather than a one-hour session
 * cache that only invalidated on login — a demotion now takes effect immediately
 * instead of up to an hour later.
 */

/** Matches `CheckFn` in the package: a truthy result decides, `false` abstains. */
type AnyCheck = (
  user: any,
  payload: any,
  perms: { grants: Map<string, string[]> },
) => Promise<{ allowed: boolean; reason: string } | false>;

export interface SaraaScopeConfig<S extends string, A extends readonly string[], D, U extends { _id: string }> {
  scope: S;
  actions: A;
  typePrefix: string;
  checks?: Partial<Record<A[number], Record<string, AnyCheck>>>;
  fetchUser: () => Promise<Maybe<U>>;
  noUserHandler: () => Promise<{ allowed: boolean; reason: string }>;
}

export interface SaraaScopeResult<A extends readonly string[], D, U extends { _id: string }> {
  policy: IPolicy<any>;
  type: string;
  permissions: string[];
  createPayload: (payload: { action: A[number]; data?: D; user?: U }) => Record<string, unknown>;
  checkOptions: Record<string, string[]>;
}

export const defineSaraaScopePolicy = <D, U extends { _id: string }>() => {
  return <S extends string, A extends readonly string[]>(
    config: SaraaScopeConfig<S, A, D, U>,
  ): SaraaScopeResult<A, D, U> => {
    const type = `${config.typePrefix}/${config.scope}`;
    const permissions = config.actions.map(action => permissionFor(config.scope, action));

    const createPayload = (payload: { action: A[number]; data?: D; user?: U }) => ({ type, ...payload });

    const policy: IPolicy<any> = {
      name: `${config.scope}Policy`,
      acceptingPayloadTypes: [type],

      async evaluate({ payload }: { payload: { action: A[number]; data?: D; user?: U } }) {
        const permission = permissionFor(config.scope, payload.action);

        // On the server a caller who already holds the user hands it over rather
        // than paying for a second lookup — same precedence as the package.
        const user = A.isServer ? (payload.user ?? (await config.fetchUser())) : await config.fetchUser();
        if (!user) return config.noUserHandler();

        const perms = { grants: resolveGrantsFor(user as never) };
        const checks = perms.grants.get(permission);
        if (!checks) return { allowed: false, reason: `No grant for ${permission}` };
        if (checks.length === 0) return { allowed: true, reason: `${permission} granted globally` };

        for (const checkName of checks) {
          const check = config.checks?.[payload.action]?.[checkName];
          // A grant naming a check the scope never registered would silently fall
          // through to a denial; `assertCheckNamesExist` rejects that at boot.
          if (!check) continue;
          const result = await check(user, payload, perms);
          if (result) return result;
        }

        return { allowed: false, reason: `No check passed for ${permission}` };
      },
    };

    const checkOptions: Record<string, string[]> = {};
    for (const action of config.actions) {
      const names = config.checks?.[action as A[number]] ? Object.keys(config.checks[action as A[number]]!) : [];
      if (names.length > 0) checkOptions[permissionFor(config.scope, action)] = names;
    }

    return { policy, type, permissions, createPayload, checkOptions };
  };
};
